import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { SectionNotificationType } from 'wix-ui-tpa/cssVars';
import { type AdUnit } from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import { ConnectStatus } from '@wix/ambassador-adsense-v1-adsense-user/types';
import { type FetchStatus } from '@app/store/settings/types';

interface WidgetState {
  adUnit?: AdUnit;
  connectStatus?: ConnectStatus;
}

const initialState: WidgetState = {
  adUnit: undefined,
  connectStatus: ConnectStatus.DISCONNECTED,
};

const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {
    setAdUnit(state, action: PayloadAction<AdUnit | undefined>) {
      state.adUnit = action.payload;
    },
    setConnectStatus(state, action: PayloadAction<ConnectStatus | undefined>) {
      state.connectStatus = action.payload;
    },
  },
});

// Selectors
const selectWidgetState = (state: { widget: WidgetState }) => state.widget;

const selectAdUnit = createSelector(
  [selectWidgetState],
  (widgetState) => widgetState.adUnit,
);

const selectConnectStatus = createSelector(
  [selectWidgetState],
  (widgetState) => widgetState.connectStatus,
);

const selectNotificationType = createSelector(
  [selectConnectStatus, selectAdUnit, selectWidgetState],
  (connectStatus = ConnectStatus.DISCONNECTED, adUnit) => {
    if (connectStatus === ConnectStatus.CONNECTED) {
      if (adUnit?.reportingDimensionId) {
        return SectionNotificationType.success;
      }
      return SectionNotificationType.alert;
    }
    if (connectStatus === ConnectStatus.IN_REVIEW) {
      return SectionNotificationType.alert;
    }
    return SectionNotificationType.default;
  },
);

export const { setAdUnit, setConnectStatus } =
  widgetSlice.actions;

export default widgetSlice.reducer;

export type { WidgetState, FetchStatus };
export { selectAdUnit, selectConnectStatus, selectNotificationType };
